// typescript
import React from "react";
import GroupArchivedBanner from "@/main/react/group/GroupArchivedBanner";
import { readGroupActivityFromDataset } from "@/main/common/GroupActivityTypes";
import { GroupInviteRequestsSection } from "@/main/react/page/group-page/GroupInviteRequestsSection";
import { createReactMount } from "@/main/react/mountContract";
import { ClientFactory } from "@/main/common/ClientFactory";
import { MetaHandler } from "@/main/common/MetaHandler";
import { GroupRail } from "@/main/react/group/GroupRailLazy";
import { BannedUserDTO, DiscussionWithoutContentDTO } from "@/generated-client";
import {
    onDiscussionAddedToGroup,
    onDiscussionRemovedFromGroup,
    onGroupDiscussionSubmissionResolved,
    onGroupInvitationAdded,
    onGroupInvitationRemoved,
    onGroupJoinRequestRemoved,
    onGroupJoinRequestSubmitted,
    onGroupLeaderAdded,
    onGroupLeaderRemoved,
    onGroupSettingsUpdated,
} from "@/main/common/EventRegistry";
import { BannedUserCardDisplayer } from "@/main/react/card/ViewerCardDisplayer";
import {
    GroupBannedUsersSection,
    GroupExcludedDiscussionsSection,
    GroupModerationLogSection,
    GroupReportsSection,
    GroupTimeoutsSection,
} from "@/main/react/group/GroupModerationSections";
import logger from "@/lib/logger";
import SensitivePageGate from "@/main/react/sensitive/SensitivePageGate";
import { useSensitivePageUnlock } from "@/main/react/sensitive/useSensitivePageUnlock";
import { getPageContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import {
    GroupContentOrderingControls,
    GroupManageConsoleHeader,
    GroupMobileHeader,
    GroupPageHeading,
    GroupTabBar,
    groupWallOwnsJoinActions,
} from "./group-page/GroupPageHeaders";
import GroupButtons from "@/main/react/group/GroupButtons";
import { useGroupInviteLanding } from "@/main/react/group/invite/useGroupInviteLanding";
import CreateGroupForm from "@/main/react/group/CreateGroupFormLazy";
import { emitGroupSettingsUpdated } from "@/main/common/EventRegistry";
import type { GroupWelcomeContent } from "@/main/react/group/GroupWelcomeDialog";
import {
    DiscussionsSection,
    DiscussionSubmissionsSection,
    GroupRelatedGroupsAddRow,
    InvitationsSection,
    LeadersSection,
    MembersSection,
    RequestsSection,
    ViewerRequestsSection,
} from "./group-page/GroupPageSectionLists";
import {
    buildGroupSectionPath,
    getGroupDiscussionOrderingOptions,
    getGroupManageTools,
    getGroupMemberTabs,
    getGroupPendingLeaderActionCount,
    GroupDiscussionOrderingStyle,
    GroupLeaderIdentity,
    GroupPageProps,
    GROUP_DEFAULT_SECTION,
    isGroupManageSection,
    normalizeGroupDiscussionOrdering,
    resolveLegacyGroupSection,
} from "./group-page/GroupPageShared";
import { PageWithRailLayout } from "@/main/react/page/PageWithRailLayout";
import { MAIN_PAGE_LIST_WIDTH_CLASS } from "@/main/react/list/MainPageListLayout";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import { useHistoryBackedState } from "@/main/react/page/useHistoryBackedState";
import { normalizeDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { PageViewTracker } from "@/main/common/PageViewTracker";
import {
    GroupGovernanceBootstrap,
    GroupLeaderQueueCounts,
    GroupRailSectionUpdate,
    GroupResourceLike,
    GroupRuleLike,
    normalizeLeaderQueueCounts,
    normalizePinnedDiscussions,
    PinnedGroupDiscussionLike,
    RelatedGroupLike,
} from "@/main/react/group/GroupGovernanceTypes";
import type { BannedUserWithActorAvatar } from "@/main/common/UserAvatarContracts";
import { GroupFeaturedDiscussionsBlock } from "./group-page/GroupFeaturedDiscussionsBlock";
import { GroupManageConsoleIndex } from "./group-page/GroupManageConsoleIndex";
import { buildGroupChatUrl } from "./group-page/groupChatLink";
import { normalizeGroupDiscussionSubmissionPolicy } from "@/main/react/group/GroupDiscussionSubmissionPolicy";
import { useFloatingActionGroup } from "@/main/react/floating-actions/FloatingActionContributionStore";
import { showAddDiscussionsToGroupDialog } from "@/main/react/Dialog/GroupAddDiscussionToGroupDialog";
import { ArrowUp, Eye, Lock, PlusSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Localizer } from "@/main/react/Types";
import { CREATE_DISCUSSION_ACTION, type FloatingActionDescriptor } from "@/main/react/floating-actions/FloatingActionConfig";
import { prefersReducedMotion } from "@/main/util/motionPreference";

/**
 * `GROUP-80` (S7 U27): the leader's "View as a visitor" control on the About tab and, while previewing,
 * its way out. One component, the profile's FI-618 `ProfileVisitorPreviewBar` idiom, so the control and
 * its exit can never be shown together or both be missing. One home for the whole tab, not one per card.
 */
function GroupAboutVisitorPreviewBar({ previewing, onChange, localizer }: {
    previewing: boolean;
    onChange: (asVisitor: boolean) => void;
    localizer: Localizer;
}) {
    return (
        <div className="flex justify-end" data-playwright-id="group-about-visitor-preview">
            {previewing ? (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-playwright-id="group-about-visitor-preview-exit"
                    onClick={() => onChange(false)}
                >
                    {localizer("profile.visitor_preview.exit")}
                </Button>
            ) : (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-playwright-id="group-about-visitor-preview-open"
                    onClick={() => onChange(true)}
                >
                    <Eye className="size-4" aria-hidden="true" />
                    {localizer("group.about.view_as_visitor")}
                </Button>
            )}
        </div>
    );
}

/** One scope id for the group page's corner contribution — see the block that publishes it. */
const GROUP_PAGE_FLOATING_SCOPE = "group-page";

type GroupRouteState = {
    section: string;
    orderingStyle: GroupDiscussionOrderingStyle;
    /**
     * The Questions filter chip on the discussions section (`?questions=1`). A REFINEMENT of the list
     * already on screen, not a destination — so it replaces the history entry rather than pushing one.
     */
    questionsOnly: boolean;
};

function normalizeLeadershipLevel(value: unknown): GroupLeaderIdentity["leadershipLevel"] {
    switch (value) {
        case "OWNER":
        case "ADMINISTRATOR":
        case "MODERATOR":
            return value;
        default:
            return undefined;
    }
}

function normalizeGovernanceBootstrap(raw: unknown): GroupGovernanceBootstrap | undefined {
    if (!raw || typeof raw !== "object") {
        return undefined;
    }

    const candidate = raw as GroupGovernanceBootstrap;
    return {
        rules: Array.isArray(candidate.rules) ? candidate.rules : [],
        resources: Array.isArray(candidate.resources) ? candidate.resources : [],
        relatedGroups: Array.isArray(candidate.relatedGroups) ? candidate.relatedGroups : [],
        leaders: Array.isArray(candidate.leaders)
            ? candidate.leaders.map((leader) => ({
                username: leader?.username,
                profileImageUrl: leader?.profileImageUrl ?? undefined,
                leadershipLevel: normalizeLeadershipLevel(leader?.leadershipLevel),
                // Kept for the header's sensitive-avatar mask (round 39 `1d`, D18). The SSR payload emits
                // it via `GroupLeaderProps.sensitive`; anything but `true` normalizes to not sensitive.
                sensitive: (leader as { sensitive?: unknown } | null | undefined)?.sensitive === true ? true : undefined,
            }))
            : [],
        pinnedDiscussions: Array.isArray(candidate.pinnedDiscussions)
            ? normalizePinnedDiscussions(candidate.pinnedDiscussions)
            : [],
        pinnedDiscussionsLimit: typeof candidate.pinnedDiscussionsLimit === "number"
            && Number.isFinite(candidate.pinnedDiscussionsLimit)
            ? candidate.pinnedDiscussionsLimit
            : null,
        welcomeMessage: typeof candidate.welcomeMessage === "string" ? candidate.welcomeMessage : null,
        topics: Array.isArray(candidate.topics)
            ? candidate.topics.filter((topic): topic is string => typeof topic === "string")
            : [],
        leaderQueueCounts: normalizeLeaderQueueCounts(candidate.leaderQueueCounts),
    };
}

export function GroupPage(props: GroupPageProps) {
    const {
        initialSection = "discussions",
        groupPublicId,
        groupSlug,
        initialName,
        initialDescription,
        initialThumbnailUrl,
        initialBannerUrl,
        mainLocale,
        initialDiscussionSubmissionPolicy,
        initialCreatorUsername,
        initialCreatorProfileImageUrl,
        initialCreatedAt,
        initialActivity,
        initialDiscussionsCount,
        initiallyMember,
        initiallyLeader,
        viewerLeadershipLevel,
        initiallyRequested,
        initiallyBanned,
        privateAccess,
        sensitive = false,
        banned,
        deniedAccess = false,
        archived = false,
        initialGovernanceBootstrap,
        localizer
    } = props;
    const normalizedInitialGovernanceBootstrap = React.useMemo(
        () => normalizeGovernanceBootstrap(initialGovernanceBootstrap),
        [initialGovernanceBootstrap],
    );
    const hasInitialGovernanceBootstrap = Boolean(normalizedInitialGovernanceBootstrap);
    const isLoggedIn = MetaHandler.isUserLoggedIn();
    // Chat completeness §F18 (D25, D27): a leader's invite link lands HERE, as `?groupInvite=<code>&sv_s=`.
    // The hook strips the code and loads the landing only when one is present.
    useGroupInviteLanding({ groupPublicId: groupPublicId ?? "", groupSlug: groupSlug ?? "", groupName: initialName, deniedAccess, localizer });
    // Invitation management is privacy-aware: administrators/owners always, plus moderators in
    // private groups. This mirrors GroupService.assertViewerCanManageInvitations on the backend.
    const canManageInvitations = viewerLeadershipLevel === "OWNER"
        || viewerLeadershipLevel === "ADMINISTRATOR"
        || (privateAccess && viewerLeadershipLevel === "MODERATOR");
    // Editing settings requires ADMINISTRATOR or OWNER on the backend; mirror that here so the
    // console never advertises a tool the save would reject.
    const canEditSettings = viewerLeadershipLevel === "OWNER" || viewerLeadershipLevel === "ADMINISTRATOR";
    const memberTabs = React.useMemo(
        () => getGroupMemberTabs(localizer, { deniedAccess }),
        [deniedAccess, localizer],
    );
    /**
     * Where `Open chat` points (design §5.1). Null for an anonymous visitor, who has no Messages, and
     * null with no group id.
     *
     * <p><b>This is a URL, not a fetch, and that is the whole change.</b> What stood here was the
     * `getGroupBoard` call that ran on every group page load for every logged-in member to draw a
     * `Board · N` pill — 1,867 requests over 8 logged days for a number that had been zero for all but
     * five people, ever. The conversation moved to Messages, so the group page now links to it and
     * asks the server nothing.
     */
    const openChatHref = React.useMemo(
        () => (isLoggedIn && !deniedAccess ? buildGroupChatUrl({ groupPublicId }) : null),
        [deniedAccess, groupPublicId, isLoggedIn],
    );
    /**
     * The manage console's four queue sizes, or null when the viewer is not a leader (or arrived
     * without a bootstrap, on the AJAX fallback path). Null renders no badges anywhere — never zeros.
     *
     * <p>Seeded once from the server and then corrected by the events the console already emits; see
     * `useGroupLeaderQueueCounts` below. There is deliberately no poll: this is a nudge, not a live
     * dashboard.
     */
    const [leaderQueueCounts, setLeaderQueueCounts] = React.useState<GroupLeaderQueueCounts | null>(
        () => normalizedInitialGovernanceBootstrap?.leaderQueueCounts ?? null,
    );
    const manageTools = React.useMemo(
        () => (deniedAccess ? [] : getGroupManageTools(localizer, { initiallyLeader, canManageInvitations, canEditSettings, queueCounts: leaderQueueCounts })),
        [canEditSettings, canManageInvitations, deniedAccess, initiallyLeader, leaderQueueCounts, localizer],
    );
    const pendingLeaderActionCount = React.useMemo(
        () => getGroupPendingLeaderActionCount(leaderQueueCounts),
        [leaderQueueCounts],
    );
    const orderingOptions = React.useMemo(() => getGroupDiscussionOrderingOptions(localizer), [localizer]);
    // "metrics" is reachable from the console sub-nav but is its own page, so it is deliberately not
    // an allowed in-shell section — handleSectionChange navigates instead of switching to it.
    const allowedSections = React.useMemo(
        () => new Set([
            ...memberTabs.map((tab) => tab.value),
            ...manageTools.map((tool) => tool.value).filter(isGroupManageSection),
        ]),
        [manageTools, memberTabs],
    );
    const defaultManageSection = React.useMemo(
        () => manageTools.map((tool) => tool.value).find(isGroupManageSection),
        [manageTools],
    );
    const normalizeSection = React.useCallback((candidate?: string) => {
        if (deniedAccess) return GROUP_DEFAULT_SECTION;
        const resolved = resolveLegacyGroupSection(candidate);
        if (resolved && allowedSections.has(resolved)) return resolved;
        // A console URL the viewer can't open (leadership lost, or a tool they lack) falls back to
        // the first tool they DO have, and only then to the group itself.
        if (isGroupManageSection(resolved) && defaultManageSection) return defaultManageSection;
        return memberTabs[0]?.value ?? GROUP_DEFAULT_SECTION;
    }, [allowedSections, defaultManageSection, deniedAccess, memberTabs]);
    const [groupName, setGroupName] = React.useState(initialName);
    const [groupDescription, setGroupDescription] = React.useState(initialDescription ?? null);
    const [groupThumbnailUrl, setGroupThumbnailUrl] = React.useState(initialThumbnailUrl ?? null);
    const [groupBannerUrl, setGroupBannerUrl] = React.useState(initialBannerUrl ?? null);
    const [groupMainLocale, setGroupMainLocale] = React.useState(mainLocale ?? null);
    const [groupDiscussionSubmissionPolicy, setGroupDiscussionSubmissionPolicy] = React.useState(
        normalizeGroupDiscussionSubmissionPolicy(initialDiscussionSubmissionPolicy),
    );
    const [groupPrivateAccess, setGroupPrivateAccess] = React.useState(privateAccess);
    const [groupSensitive, setGroupSensitive] = React.useState(sensitive);
    /**
     * `ERRORS-24` / wave-14 `Q10`: whether the wall panel below owns this page's join actions, so the
     * two header components withhold theirs. ONE predicate, three readers — see
     * `groupWallOwnsJoinActions` in `GroupPageHeaders` for why it is derived from the group's own
     * facts rather than from `deniedAccess`, and why the safe failure direction runs this way round.
     */
    const wallOwnsJoinActions = groupWallOwnsJoinActions({
        privateAccess: groupPrivateAccess,
        initiallyMember,
        initiallyLeader,
        initiallyBanned,
    });
    const [groupRules, setGroupRules] = React.useState<GroupRuleLike[]>(() => normalizedInitialGovernanceBootstrap?.rules ?? []);
    const [groupResources, setGroupResources] = React.useState<GroupResourceLike[]>(() => normalizedInitialGovernanceBootstrap?.resources ?? []);
    const [groupRelatedGroups, setGroupRelatedGroups] = React.useState<RelatedGroupLike[]>(() => normalizedInitialGovernanceBootstrap?.relatedGroups ?? []);
    const [groupLeaders, setGroupLeaders] = React.useState<GroupLeaderIdentity[]>(() => normalizedInitialGovernanceBootstrap?.leaders ?? []);
    const [pinnedDiscussions, setPinnedDiscussions] = React.useState<PinnedGroupDiscussionLike[]>(() => normalizedInitialGovernanceBootstrap?.pinnedDiscussions ?? []);
    const [groupWelcomeMessage, setGroupWelcomeMessage] = React.useState<string | null>(
        () => normalizedInitialGovernanceBootstrap?.welcomeMessage ?? null,
    );
    const [groupTopics, setGroupTopics] = React.useState<string[]>(
        () => normalizedInitialGovernanceBootstrap?.topics ?? [],
    );
    // Content for the new-joiner welcome dialog and the "About this group" overflow entry. Assembled
    // from live state so an owner who edits rules/resources/greeting sees the change without a reload.
    // Topics come from the same governance aggregate as the rules and resources, so the Topics
    // section costs no extra request.
    const groupWelcomeContent = React.useMemo<GroupWelcomeContent>(() => ({
        groupName: groupName ?? "",
        groupSlug,
        description: groupDescription,
        bannerUrl: groupBannerUrl,
        thumbnailUrl: groupThumbnailUrl,
        ownerName: initialCreatorUsername ?? null,
        welcomeMessage: groupWelcomeMessage,
        rules: groupRules,
        resources: groupResources,
        topics: groupTopics,
    }), [
        groupName,
        groupSlug,
        groupDescription,
        groupBannerUrl,
        groupThumbnailUrl,
        initialCreatorUsername,
        groupWelcomeMessage,
        groupRules,
        groupResources,
        groupTopics,
    ]);
    const [groupLoading, setGroupLoading] = React.useState(() => !deniedAccess && Boolean(groupPublicId) && !hasInitialGovernanceBootstrap);
    const [groupError, setGroupError] = React.useState<string | null>(null);
    const groupLoadRequestIdRef = React.useRef(0);
    const groupApi = React.useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);
    const paginationApi = React.useMemo(() => ClientFactory.getAjaxPaginationRestControllerApi(), []);

    const loadGroupModules = React.useCallback(async () => {
        const requestId = ++groupLoadRequestIdRef.current;

        if (deniedAccess) {
            if (requestId === groupLoadRequestIdRef.current) {
                setGroupRules([]);
                setGroupResources([]);
                setGroupRelatedGroups([]);
                setGroupLeaders([]);
                setPinnedDiscussions([]);
                setGroupLoading(false);
                setGroupError(null);
            }
            return;
        }

        if (!groupPublicId) {
            if (requestId === groupLoadRequestIdRef.current) {
                setGroupRules([]);
                setGroupResources([]);
                setGroupRelatedGroups([]);
                setGroupLeaders([]);
                setPinnedDiscussions([]);
                setGroupLoading(false);
                setGroupError(null);
            }
            return;
        }

        setGroupLoading(true);
        setGroupError(null);

        try {
            const [rulesResp, resourcesResp, relatedResp, leadersResp] = await Promise.all([
                groupApi.listGroupRules?.(groupPublicId),
                groupApi.listGroupResources?.(groupPublicId),
                groupApi.listRelatedGroups?.(groupPublicId),
                paginationApi.groupLeaders?.(groupPublicId),
            ]);
            const pinnedResp = await ClientFactory.listPinnedDiscussions(groupPublicId);

            if (requestId !== groupLoadRequestIdRef.current) return;

            setGroupRules(rulesResp?.data ?? []);
            setGroupResources(resourcesResp?.data ?? []);
            setGroupRelatedGroups(relatedResp?.data ?? []);
            setGroupLeaders(leadersResp?.data ?? []);
            setPinnedDiscussions(normalizePinnedDiscussions(pinnedResp?.data ?? []));
        } catch (e) {
            if (requestId !== groupLoadRequestIdRef.current) return;

            setGroupRules([]);
            setGroupResources([]);
            setGroupRelatedGroups([]);
            setGroupLeaders([]);
            setPinnedDiscussions([]);
            setGroupError(localizer("group.about.load_failed"));
            logger.error("[GroupPage] fetch group modules error", e);
        } finally {
            if (requestId === groupLoadRequestIdRef.current) {
                setGroupLoading(false);
            }
        }
    }, [deniedAccess, groupApi, groupPublicId, localizer, paginationApi]);

    /**
     * Read the section from the URL. Two shapes: a single member-tab segment, or the two-segment
     * console form `/group/{slug}/manage/{tool}` — rejoined so the rest of the page keeps working
     * with one flat section string.
     */
    const getSectionFromPath = React.useCallback((pathname: string) => {
        const parts = pathname.split("/").filter(Boolean).map(decodeURIComponent);
        const idx = parts.indexOf("group");
        if (idx === -1) return initialSection;
        const first = parts[idx + 2];
        if (!first) return initialSection;
        if (first === "manage") {
            return parts[idx + 3] ? `manage/${parts[idx + 3]}` : "manage";
        }
        return first;
    }, [initialSection]);
    const initialRouteState = React.useMemo<GroupRouteState>(() => ({
        section: normalizeSection(initialSection),
        orderingStyle: normalizeGroupDiscussionOrdering(),
        questionsOnly: false,
    }), [initialSection, normalizeSection]);

    const { state: routeState, setState: setRouteState } = useHistoryBackedState<GroupRouteState>({
        initialState: initialRouteState,
        readState: (location, fallback) => ({
            section: normalizeSection(getSectionFromPath(location.pathname) ?? fallback.section),
            orderingStyle: normalizeGroupDiscussionOrdering(new URLSearchParams(location.search).get("ordering") ?? fallback.orderingStyle),
            // Not fallback-backed: only the CURRENT url decides whether the list is filtered, so a Back
            // that drops the param unfilters it.
            questionsOnly: new URLSearchParams(location.search).get("questions") === "1",
        }),
        buildUrl: (state, location) => {
            const params = new URLSearchParams(location.search);

            if (state.orderingStyle === "active") {
                params.delete("ordering");
            } else {
                params.set("ordering", state.orderingStyle);
            }

            // Only when narrowed: an unfiltered group URL stays byte-identical to what it was before
            // the chip existed, so existing links and bookmarks keep meaning "everything".
            if (state.questionsOnly) {
                params.set("questions", "1");
            } else {
                params.delete("questions");
            }

            if (!groupSlug) {
                const query = params.toString();
                return `${location.pathname}${query ? `?${query}` : ""}${location.hash}`;
            }

            const normalizedSection = normalizeSection(state.section);
            const query = params.toString();
            return `${buildGroupSectionPath(groupSlug, normalizedSection)}${query ? `?${query}` : ""}${location.hash}`;
        },
        // Every field must be compared here. `useHistoryBackedState` treats an "equal" next state as
        // a no-op — so a field omitted from this comparison can never change, and the symptom is
        // silent: the state simply never changes, with no error.
        areEqual: (left, right) =>
            left.section === right.section
            && left.orderingStyle === right.orderingStyle
            && left.questionsOnly === right.questionsOnly,
    });
    const section = routeState.section;
    const orderingStyle = routeState.orderingStyle;
    const questionsOnly = routeState.questionsOnly;
    const inManageConsole = isGroupManageSection(section);

    /*
     * `GROUP-80` (S7 U27): a leader's About tab doubles as its editor, so the one person responsible for
     * the group's pitch never saw it. "View as a visitor" swaps THIS render to the visitor's (no route,
     * no history entry: a second address would be one more entry to Back out of). It is a view state of
     * the About tab only, so it ends when the tab does.
     */
    const [aboutAsVisitor, setAboutAsVisitor] = React.useState(false);
    React.useEffect(() => {
        if (section !== "about") {
            setAboutAsVisitor(false);
        }
    }, [section]);
    const aboutLeaderView = Boolean(initiallyLeader) && !aboutAsVisitor;

    /*
     * `EMPTY-STATES-22` (operator decision, wave-6 §4 D6, applied wave 8 2026-08-22): the GROUP PAGE
     * contributes its own floating group, so the corner action on a group means "add a post to THIS
     * group".
     *
     * What it fixed. The static registry resolves the corner to a catch-all `create-discussion` whose
     * rule is `{match: () => true}`, carrying no group context and navigating to a bare
     * `/create/discussion`. On an empty group that made the largest, most primary-coloured control in
     * the frame (a 56px `#0A55B8` circle on desktop, ~72 CSS px on a phone) the one control that
     * LEAVES the group - while the page's own "Add the first post" was a 120x32 pill inside the dashed
     * panel. A leader reaching for the obvious blue button wrote a post that does not land here unless
     * they remember to attach it in the publish panel, and the recovery is this same picker, later.
     *
     * Why this dialog and not a seeded composer: `showAddDiscussionsToGroupDialog` is the same path
     * the empty state's own primary opens, it is group-scoped by construction, and composing is one
     * level in from its authored tab. The composer has no group-seed parameter today, so a seeded
     * `/create/discussion` would be a backend + composer slice, not a corner-button fix.
     *
     * The row FORBIDS the third option - hiding the FAB on a route pattern - and that prohibition is
     * documented in `FloatingActionConfig.ts` itself. Contributing `null` when the viewer cannot add
     * is not that: it hands the corner back to the static catch-all, which is the right control for
     * an anonymous visitor or a non-member.
     */
    const canAddToThisGroup = Boolean(groupPublicId) && (initiallyMember || initiallyLeader) && !inManageConsole;

    /**
     * FI-609 / QC-15: Back to top on the Posts feed, HomePage's `HOME-FEED-29` pattern. The ordering
     * controls sit in page flow above an endlessly appending list, so once they scroll away a neutral
     * jump back to them joins the corner. It is folded into THIS page's one floating group, because the
     * store resolves a single winning group: publishing it as a second scope would hide "Add post"
     * behind it. A viewer who cannot add gets it beside the re-declared Create instead, since a
     * contributed group replaces the static catch-all (`CREATE_DISCUSSION_ACTION`'s own note).
     * No key is minted: `home.controls.back-to-top` is the same action's word on the other long lists.
     */
    const controlRowRef = React.useRef<HTMLDivElement | null>(null);
    const [controlsOutOfView, setControlsOutOfView] = React.useState(false);
    const jumpApplies = !deniedAccess && section === "discussions";

    React.useEffect(() => {
        setControlsOutOfView(false);
        const node = controlRowRef.current;
        if (!jumpApplies || !node || typeof IntersectionObserver === "undefined") {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setControlsOutOfView(!entry.isIntersecting),
            { threshold: 0 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [jumpApplies]);

    const scrollToControlRow = React.useCallback(() => {
        controlRowRef.current?.scrollIntoView({
            behavior: prefersReducedMotion() ? "auto" : "smooth",
            block: "start",
        });
    }, []);

    /*
     * `GROUP-34` / `GROUP-123` (U20, mega solo `mr2-group`): the "Add post" corner button is a SECOND copy of
     * the hero's own Add post, so it appears only once that hero button has scrolled away. While the hero's
     * Add post is on screen the page contributes an EMPTY group, which keeps the corner clear rather than
     * handing it back to the catch-all Create (and so stops the FAB sitting on roster ⋯ buttons, About
     * pencils and the first post at 411 px). No hero button found (a render without the header) keeps
     * today's behaviour: the corner shows Add post.
     */
    const [heroAddPostVisible, setHeroAddPostVisible] = React.useState(false);
    React.useEffect(() => {
        setHeroAddPostVisible(false);
        if (!canAddToThisGroup || typeof IntersectionObserver === "undefined") {
            return;
        }
        const targets = Array.from(document.querySelectorAll('[data-playwright-id="group-add-discussion"]'));
        if (targets.length === 0) {
            return;
        }
        setHeroAddPostVisible(true);
        const onScreen = new Set<Element>();
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    onScreen.add(entry.target);
                } else {
                    onScreen.delete(entry.target);
                }
            });
            setHeroAddPostVisible(onScreen.size > 0);
        }, { threshold: 0 });
        targets.forEach((target) => observer.observe(target));
        return () => observer.disconnect();
    }, [canAddToThisGroup, section]);

    const showBackToTop = jumpApplies && controlsOutOfView;
    const floatingActions = React.useMemo<FloatingActionDescriptor[]>(() => {
        const actions: FloatingActionDescriptor[] = [];
        if (showBackToTop) {
            actions.push({
                id: "group-back-to-controls",
                look: {
                    icon: ArrowUp,
                    labelKey: "home.controls.back-to-top",
                    variant: "neutral",
                    size: "md",
                },
                onClick: scrollToControlRow,
                dataPlaywrightId: "floating-action-group-back-to-controls",
            });
        }
        if (canAddToThisGroup && groupPublicId && !heroAddPostVisible) {
            actions.push({
                id: "add-post-to-group",
                look: {
                    icon: PlusSquare,
                    labelKey: "group.add.discussion",
                    variant: "primary",
                    size: "lg",
                },
                onClick: () => showAddDiscussionsToGroupDialog(groupPublicId, localizer),
                dataPlaywrightId: "floating-action-add-post-to-group",
            });
        } else if (showBackToTop && !canAddToThisGroup) {
            actions.push(CREATE_DISCUSSION_ACTION);
        }
        return actions;
    }, [canAddToThisGroup, groupPublicId, heroAddPostVisible, localizer, scrollToControlRow, showBackToTop]);

    useFloatingActionGroup(
        GROUP_PAGE_FLOATING_SCOPE,
        // U20: a viewer who can add always contributes (possibly empty), so the corner never falls back
        // to the catch-all Create while the hero's own Add post is on screen.
        floatingActions.length > 0 || canAddToThisGroup
            ? {
                scopeId: GROUP_PAGE_FLOATING_SCOPE,
                actions: floatingActions,
            }
            : null,
        [floatingActions],
    );

    /**
     * Every section except one is a panel inside this shell, so switching is client-side state.
     * `metrics` is a separate leader-only PAGE (GroupController renders its own template), so it
     * has to be a real navigation — setting route state for it would leave the shell showing an
     * empty panel. Keep this the single entry point for section changes, so a future section that
     * is also its own page only has to be handled here.
     */
    const handleSectionChange = React.useCallback((value: string) => {
        if (value === "metrics") {
            if (groupSlug) {
                navigateToUrl(buildGroupSectionPath(groupSlug, "metrics"), { source: "group-section-metrics" });
            }
            return;
        }
        setRouteState((previous) => ({ ...previous, section: normalizeSection(value) }));
    }, [groupSlug, normalizeSection, setRouteState]);

    /**
     * `GROUP-19`: the real address of a member section, so the tab strip and the header count chips
     * can be ANCHORS rather than buttons — middle-click, open-in-new-tab, copy-link-address,
     * long-press and a rendering crawler all key on an href being in the DOM, and none of them can be
     * had from a `<button>` however well it navigates.
     *
     * <p>Built from `buildGroupSectionPath`, which is the same builder this page's own
     * `useHistoryBackedState` `toUrl` uses above — so "what middle-click opens is by construction the
     * address a plain click produces" (the constraint `DISCUSSION-14`'s closure records) holds by
     * construction rather than by agreement.
     *
     * <p>Returns `undefined` with no slug, and for `metrics`, which is a separate leader-only page
     * rather than a section of this shell — the controls that would link to it are not these ones,
     * and emitting a section-shaped href for it would be a lie.
     */
    const buildSectionHref = React.useCallback((value: string): string | undefined => {
        if (!groupSlug || value === "metrics") {
            return undefined;
        }
        return buildGroupSectionPath(groupSlug, normalizeSection(value));
    }, [groupSlug, normalizeSection]);

    /**
     * Sorting PUSHES (§13.1). §11.5 made it replace to keep the escape cost down; §13.2 retired that
     * argument — the cost is real and is now accepted (D2), because the alternative is a member having
     * to learn which of a page's controls are "real".
     */
    const handleOrderingStyleChange = React.useCallback((value: string) => {
        setRouteState((previous) => ({ ...previous, orderingStyle: normalizeGroupDiscussionOrdering(value) }));
    }, [setRouteState]);

    /**
     * The Questions filter PUSHES, for exactly the reason sorting does (§13.1): it changes the address,
     * so Back returns to the unfiltered list.
     */
    const handleQuestionsOnlyChange = React.useCallback((next: boolean) => {
        setRouteState((previous) => ({ ...previous, questionsOnly: next }));
    }, [setRouteState]);

    /**
     * Clear the Questions filter from inside the filtered-to-zero panel (`EMPTY-STATES-20`).
     *
     * <p>Stable identity so the section below does not re-render on every parent render, and it goes
     * through `handleQuestionsOnlyChange` rather than touching route state itself — that handler is the
     * one place the replace-not-push decision is written down.
     */
    const handleClearQuestionsFilter = React.useCallback(() => {
        handleQuestionsOnlyChange(false);
    }, [handleQuestionsOnlyChange]);

    /**
     * Round 40 D10 (`EMPTY-STATES-122`): the Posts list is KNOWN to have no posts with the Questions chip
     * off. The chip and the sort are then withheld, since neither can change an empty result; they stay
     * while the chip is on (it is the way back out) and return when the list reports a row. Set only by
     * `DiscussionsSection`, whose list reports it once settled; a loading or failed list says nothing.
     */
    const [discussionsKnownEmpty, setDiscussionsKnownEmpty] = React.useState(false);
    const withholdPostsNarrowing = discussionsKnownEmpty && !questionsOnly;

    const handleOpenManageConsole = React.useCallback(() => {
        if (defaultManageSection) {
            handleSectionChange(defaultManageSection);
        }
    }, [defaultManageSection, handleSectionChange]);

    const handleLeaveManageConsole = React.useCallback(() => {
        handleSectionChange(GROUP_DEFAULT_SECTION);
    }, [handleSectionChange]);

    React.useEffect(() => {
        const normalized = normalizeSection(section);
        if (normalized !== section) {
            // Mount-time normalization of a section nobody navigated to (an unknown or now-forbidden
            // one in the URL), so there is no previous address to preserve.
            setRouteState((previous) => ({ ...previous, section: normalized }), { replace: "canonicalize" });
        }
    }, [normalizeSection, section, setRouteState]);

    React.useEffect(() => {
        setGroupRules(normalizedInitialGovernanceBootstrap?.rules ?? []);
        setGroupWelcomeMessage(normalizedInitialGovernanceBootstrap?.welcomeMessage ?? null);
        setGroupTopics(normalizedInitialGovernanceBootstrap?.topics ?? []);
        setGroupResources(normalizedInitialGovernanceBootstrap?.resources ?? []);
        setGroupRelatedGroups(normalizedInitialGovernanceBootstrap?.relatedGroups ?? []);
        setGroupLeaders(normalizedInitialGovernanceBootstrap?.leaders ?? []);
        setPinnedDiscussions(normalizedInitialGovernanceBootstrap?.pinnedDiscussions ?? []);
        // Re-seeded with its siblings so a fresh bootstrap replaces any drift the event listeners
        // accumulated, rather than the two disagreeing.
        setLeaderQueueCounts(normalizedInitialGovernanceBootstrap?.leaderQueueCounts ?? null);
        setGroupLoading(!deniedAccess && Boolean(groupPublicId) && !hasInitialGovernanceBootstrap);
        setGroupError(null);
    }, [deniedAccess, groupPublicId, hasInitialGovernanceBootstrap, normalizedInitialGovernanceBootstrap]);

    React.useEffect(() => {
        if (deniedAccess || !groupPublicId || hasInitialGovernanceBootstrap) {
            return;
        }

        void loadGroupModules();
    }, [deniedAccess, groupPublicId, hasInitialGovernanceBootstrap, loadGroupModules]);

    React.useEffect(() => {
        setGroupName(initialName);
        setGroupDescription(initialDescription ?? null);
        setGroupThumbnailUrl(initialThumbnailUrl ?? null);
        setGroupBannerUrl(initialBannerUrl ?? null);
        setGroupMainLocale(mainLocale ?? null);
        setGroupDiscussionSubmissionPolicy(normalizeGroupDiscussionSubmissionPolicy(initialDiscussionSubmissionPolicy));
        setGroupPrivateAccess(privateAccess);
    }, [initialDescription, initialDiscussionSubmissionPolicy, initialName, initialThumbnailUrl, initialBannerUrl, mainLocale, privateAccess]);

    const [prependDiscussions, setPrependDiscussions] = React.useState<Array<DiscussionWithoutContentDTO>>([]);
    const [removedDiscussionIds, setRemovedDiscussionIds] = React.useState<Set<string>>(new Set());

    React.useEffect(() => {
        if (!groupPublicId) return;
        const unsubAdd = onDiscussionAddedToGroup((payload: any) => {
            try {
                if (!payload) return;
                if (payload.groupPublicId !== groupPublicId) return;
                const incoming: DiscussionWithoutContentDTO | undefined = payload.discussion;
                if (!incoming?.publicId) return;

                setPrependDiscussions(prev => {
                    if (prev.some(p => p.publicId === incoming.publicId)) return prev;
                    return [incoming, ...prev];
                });

                setRemovedDiscussionIds(prev => {
                    if (!prev.has(incoming.publicId!)) return prev;
                    const n = new Set(prev);
                    n.delete(incoming.publicId!);
                    return n;
                });
            } catch (e) {
                logger.error("[GroupPage] onDiscussionAddedToGroup handler error", e);
            }
        });

        const unsubRemove = onDiscussionRemovedFromGroup((payload: any) => {
            try {
                if (!payload) return;
                if (payload.groupPublicId !== groupPublicId) return;
                const incoming: DiscussionWithoutContentDTO | undefined = payload.discussion;
                if (!incoming?.publicId) return;

                setPrependDiscussions(prev => prev.filter(p => p.publicId !== incoming.publicId));
                setPinnedDiscussions(prev => prev.filter((discussion) => discussion.publicId !== incoming.publicId));

                setRemovedDiscussionIds(prev => {
                    if (prev.has(incoming.publicId!)) return prev;
                    const n = new Set(prev);
                    n.add(incoming.publicId!);
                    return n;
                });
            } catch (e) {
                logger.error("[GroupPage] onDiscussionRemovedFromGroup handler error", e);
            }
        });

        return () => {
            unsubAdd();
            unsubRemove();
        };
    }, [groupPublicId]);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        return onGroupSettingsUpdated((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId) {
                return;
            }

            setGroupName(payload.name);
            setGroupDescription(payload.description ?? null);
            setGroupThumbnailUrl(payload.thumbnailUrl ?? null);
            // undefined => banner untouched in this settings update; leave the current banner alone.
            if (payload.bannerUrl !== undefined) {
                setGroupBannerUrl(payload.bannerUrl ?? null);
            }
            setGroupMainLocale(payload.mainLocale ?? null);
            setGroupDiscussionSubmissionPolicy(normalizeGroupDiscussionSubmissionPolicy(payload.discussionSubmissionPolicy));
            setGroupPrivateAccess(payload.privateAccess);
            setGroupSensitive(Boolean(payload.sensitive));
        });
    }, [groupPublicId]);

    React.useEffect(() => {
        if (!groupPublicId) {
            return;
        }

        const unsubscribeLeaderAdded = onGroupLeaderAdded((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId || !payload.leader?.username) {
                return;
            }

            setGroupLeaders((previous) => {
                const nextWithoutLeader = previous.filter((leader) => leader.username !== payload.leader.username);
                return [payload.leader, ...nextWithoutLeader];
            });
        });

        const unsubscribeLeaderRemoved = onGroupLeaderRemoved((payload) => {
            if (!payload || payload.groupPublicId !== groupPublicId || !payload.username) {
                return;
            }

            setGroupLeaders((previous) => previous.filter((leader) => leader.username !== payload.username));
        });

        return () => {
            unsubscribeLeaderAdded();
            unsubscribeLeaderRemoved();
        };
    }, [groupPublicId]);

    React.useEffect(() => {
        if (!groupPublicId || deniedAccess) {
            return;
        }

        const tracker = new PageViewTracker({
            errorMessage: "Failed to record group view",
            context: { groupPublicId },
            trackView: () => ClientFactory.countGroupView(groupPublicId),
        });
        tracker.startViewTracking();

        return () => {
            tracker.stopViewTracking();
        };
    }, [deniedAccess, groupPublicId]);

    /**
     * Keeps the console badges honest after the leader acts, without a poll and without a refetch.
     *
     * <p><b>One-directional, per rule 31.</b> The sections emit; this page listens; this page never
     * writes back into a section. Two components holding the same number and syncing it both ways is
     * the 2026-07-27 header crash — the two effects do not converge, they swap.
     *
     * <p>Bails on an unchanged value by returning the SAME object, not a fresh one with equal fields.
     * `manageTools` is memoised on this state, so minting `{...current}` per event would rebuild the
     * tool array and re-render the whole console nav for no change — the standing churn rule 31
     * exists to stop.
     *
     * <p>Nothing happens when the counts are unknown (`null`): a viewer without them is not a leader,
     * and inventing a count from a delta would put a badge where the server said there is none.
     * Clamped at zero because an event may be a second copy of one this page already applied.
     */
    const adjustLeaderQueueCount = React.useCallback(
        (key: keyof GroupLeaderQueueCounts, delta: number) => {
            setLeaderQueueCounts((current) => {
                if (!current) return current;
                // A single count can also be absent on its own, not just the whole record: the fifth
                // (`inviteRequests`, 2026-09-02) is optional so that a bootstrap serialized before it
                // existed still yields the other four. The rule above applies unchanged one level down
                // — an unknown count is not a zero, and a delta must never be the thing that invents it.
                const existing = current[key];
                if (existing === undefined) return current;
                const next = Math.max(0, existing + delta);
                return next === existing ? current : { ...current, [key]: next };
            });
        },
        [],
    );

    /**
     * Bans have no client event, so the ban count is refreshed by the next page load rather than
     * live. Stated rather than hidden: banning is rare and deliberate, and the leader doing it is
     * looking straight at the list that just changed.
     */
    React.useEffect(() => {
        if (!groupPublicId || !leaderQueueCounts) {
            return;
        }

        const matchesThisGroup = (payloadGroupPublicId?: string) => (
            !payloadGroupPublicId || payloadGroupPublicId === groupPublicId
        );

        const unsubscribers = [
            onGroupDiscussionSubmissionResolved((payload) => {
                if (matchesThisGroup(payload.groupPublicId)) adjustLeaderQueueCount("discussionSubmissions", -1);
            }),
            onGroupJoinRequestSubmitted((payload) => {
                if (matchesThisGroup(payload.groupPublicId)) adjustLeaderQueueCount("joinRequests", 1);
            }),
            onGroupJoinRequestRemoved((payload) => {
                if (matchesThisGroup(payload.groupPublicId)) adjustLeaderQueueCount("joinRequests", -1);
            }),
            onGroupInvitationAdded((payload) => {
                if (matchesThisGroup(payload.groupPublicId)) adjustLeaderQueueCount("invitations", 1);
            }),
            onGroupInvitationRemoved((payload) => {
                if (matchesThisGroup(payload.groupPublicId)) adjustLeaderQueueCount("invitations", -1);
            }),
        ];

        return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
        // `leaderQueueCounts` is read only for its null-ness — the subscriptions must exist once the
        // viewer turns out to be a leader and never again after that, so its identity is not a
        // dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adjustLeaderQueueCount, groupPublicId, Boolean(leaderQueueCounts)]);

    const handleGroupSaved = React.useCallback((update: GroupRailSectionUpdate) => {
        switch (update.kind) {
            case "rule":
                setGroupRules(update.items);
                return;
            case "resource":
                setGroupResources(update.items);
                return;
            case "related":
                setGroupRelatedGroups(update.items);
                return;
        }
    }, []);

    const handlePinnedDiscussionAdded = React.useCallback((discussion: PinnedGroupDiscussionLike) => {
        setPinnedDiscussions((previous) => normalizePinnedDiscussions([
            ...previous.filter((item) => item.publicId !== discussion.publicId),
            discussion,
        ]));
    }, []);

    const handlePinnedDiscussionRemoved = React.useCallback((discussionPublicId?: string, pinId?: number | null) => {
        setPinnedDiscussions((previous) => previous.filter((item) => {
            if (pinId != null && item.pinId === pinId) {
                return false;
            }

            return item.publicId !== discussionPublicId;
        }));
    }, []);

    /**
     * The inline settings tool saves through the same form the dialog uses, so it must fire the same
     * broadcast — that is what refreshes the header, the rail and every group card on the page.
     */
    const handleGroupSettingsSaved = React.useCallback((result?: any) => {
        const saved = result?.group;
        if (!saved?.groupPublicId) {
            return;
        }

        emitGroupSettingsUpdated({
            groupPublicId: saved.groupPublicId,
            name: saved.name,
            description: saved.description ?? null,
            thumbnailUrl: saved.thumbnailUrl ?? null,
            bannerUrl: saved.bannerUrl,
            mainLocale: saved.mainLocale ?? null,
            discussionSubmissionPolicy: saved.discussionSubmissionPolicy ?? null,
            privateAccess: saved.privateAccess,
            sensitive: saved.sensitive,
        });
    }, []);

    const { revealed, reveal, sensitiveMasking } = useSensitivePageUnlock({
        kind: "group",
        id: groupPublicId,
        isSensitive: groupSensitive,
    });
    const pageWarningCopy = React.useMemo(
        () => getPageContentWarningCopy(localizer, { isSensitive: groupSensitive, kind: "group" }),
        [groupSensitive, localizer],
    );

    return (
        <SensitivePageGate
            isSensitive={groupSensitive}
            hideSensitiveMedia={MetaHandler.getHideSensitiveMedia()}
            revealed={revealed}
            onReveal={reveal}
            localizer={localizer}
            badgeLabel={pageWarningCopy.badgeLabel}
            title={pageWarningCopy.title}
            helperText={pageWarningCopy.helperText}
            revealText={pageWarningCopy.revealText}
            showEnablePreferenceButton={pageWarningCopy.showSensitivePreferenceAction}
            revealButtonDataPlaywrightId="group-page-sensitive-reveal"
            subjectLabel={groupName || undefined}
        >
            <PageWithRailLayout
                dataPlaywrightId="group-page"
                // The floating action's clearance USED to be reserved here (`pb-24 lg:pb-6`, added by
                // the 2026-07-30 ux-refine run). It moved to the layout on 2026-08-04 — the default
                // `mainClass` in `layouts/basic_layout.jte`, beside the header's own `pt-[calc(...)]`
                // inset — because the FAB is a catch-all that renders on every route, so reserving for
                // it per page meant every page had to remember. Two did; the rest reserved 0px against
                // a host reaching 72px. Do not re-add it here: it would double the gutter, and the
                // layout-level owner is what `floating-action-clearance.spec.mjs` defends.
                innerClassName="py-4 mb-2"
                gridClassName="items-start"
                mainClassName="min-w-0"
                main={(
                    // One measure for the whole column. The lists wrap themselves in
                    // MAIN_PAGE_LIST_WIDTH_CLASS while the chrome above them ran at the full grid cell,
                    // so every card sat inset ~10px from the tab strip and the console header that
                    // labels it. Nesting the same cap is idempotent.
                    <div className={`min-w-0 w-full ${MAIN_PAGE_LIST_WIDTH_CLASS}`} data-playwright-id="group-main-feed">
                        {/* The page's only <h1>, and the twin of the one group.jte server-renders
                            inside this island's container. `createRoot()` deletes that one at mount,
                            so without this the whole route renders — and is indexed — with zero
                            headings (rule 32, GROUP-2). It is first in the main column on purpose:
                            the visible name lives in the rail on desktop, which comes after the feed.
                            See GroupPageHeading for why it cannot live in either header. */}
                        <GroupPageHeading name={groupName} />
                        {/*
                          * Inside the manage console the member hero collapses to an identity strip.
                          * The console is a work surface: the full hero (banner, description,
                          * visibility copy, count chips and a primary "Add post") took ~500 of the
                          * first 850px, so the loudest control on a queue route was an authoring
                          * action and the queue itself started below the fold.
                          */}
                        {inManageConsole ? (
                            <a
                                className="standard-hover lg:hidden mb-3 flex min-w-0 items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
                                href={groupSlug ? `/group/${encodeURIComponent(groupSlug)}` : "#"}
                                data-playwright-id="group-manage-identity-strip"
                            >
                                <ProfileImage localizer={localizer}
                                    imageUrl={groupThumbnailUrl || undefined}
                                    username={groupName ?? undefined}
                                    mode="group"
                                    hoverable={false}
                                    className="size-8"
                                />
                                <span className="min-w-0 flex-1 line-clamp-2 wrap-anywhere text-sm font-semibold text-text-main">{groupName}</span>
                                {/* The hero this strip replaces is the ONLY thing on the page that
                                    states visibility, and a leader working a queue is exactly the
                                    reader who must not lose it: every decision here — approve a post,
                                    admit a member — means something different in a private group.
                                    Collapsing the hero is right; collapsing this fact away with it is
                                    not. */}
                                {groupPrivateAccess ? (
                                    <span
                                        className="ms-auto shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-neutral-text"
                                        data-playwright-id="group-manage-identity-strip-private-badge"
                                    >
                                        {localizer("group.visibility.private")}
                                    </span>
                                ) : null}
                            </a>
                        ) : null}

                        <div className={inManageConsole ? "hidden" : "lg:hidden mb-3"}>
                            <GroupMobileHeader
                                name={groupName}
                                description={groupDescription}
                                welcomeContent={groupWelcomeContent}
                                thumbnailUrl={groupThumbnailUrl} bannerUrl={groupBannerUrl}
                                mainLocale={groupMainLocale}
                                creatorUsername={initialCreatorUsername ?? null}
                                creatorProfileImageUrl={initialCreatorProfileImageUrl ?? null}
                                createdAt={initialCreatedAt ?? null}
                                activity={initialActivity}
                                discussionsCount={initialDiscussionsCount ?? null}
                                groupPublicId={groupPublicId}
                                groupSlug={groupSlug}
                                initiallyMember={initiallyMember}
                                initiallyLeader={initiallyLeader}
                                viewerLeadershipLevel={viewerLeadershipLevel}
                                initiallyRequested={initiallyRequested}
                                initiallyBanned={initiallyBanned}
                                discussionSubmissionPolicy={groupDiscussionSubmissionPolicy}
                                privateAccess={groupPrivateAccess}
                                sensitive={groupSensitive}
                                leaders={groupLeaders}
                                // `ERRORS-23`: on a denied-access view the three count chips are
                                // FACTS, not destinations — every section renders the same refusal
                                // notice and the same empty panel, so a tap changed the address, wrote
                                // a history entry and produced no visible response, and the member's
                                // next Back press then also appeared to do nothing. Withholding the
                                // handler is all it takes: `GroupHeaderCountChip` already falls
                                // through to the `GROUP-12` inert treatment when there is none. The
                                // href is withheld on the same expression so an inert chip never
                                // carries a link either (`GROUP-19`).
                                onSectionChange={deniedAccess ? undefined : handleSectionChange}
                                buildSectionHref={deniedAccess ? undefined : buildSectionHref}
                                // So a count chip pointing at the section already open says "you are
                                // here" instead of offering a switch this page's own state layer
                                // discards as an equal-state no-op (`GROUP-10`).
                                currentSection={section}
                                // `GROUP-11`: the hero collapses on every section except the one the
                                // page opens on. The default IS `discussions`, so someone who arrived
                                // through the group's own link still gets the full first impression;
                                // someone who followed a "4 members" chip or a shared /members link
                                // asked for the people and gets them near the top of the screen.
                                collapsedOnMobile={section !== "discussions"}
                                localizer={localizer}
                            />
                        </div>

                        {/* GROUP-84 (settle r42 A4): one line, on every accessible section, saying why nothing here
                            takes a new post, message or member. */}
                        {!deniedAccess && archived ? (
                            <GroupArchivedBanner localizer={localizer} />
                        ) : null}

                        {!deniedAccess && !inManageConsole ? (
                            <GroupTabBar
                                tabs={memberTabs}
                                section={section}
                                onSectionChange={handleSectionChange}
                                buildSectionHref={buildSectionHref}
                                openChatHref={openChatHref}
                                showManageEntry={Boolean(defaultManageSection)}
                                onOpenManage={handleOpenManageConsole}
                                pendingLeaderActionCount={pendingLeaderActionCount}
                                localizer={localizer}
                            />
                        ) : null}

                        {!deniedAccess && inManageConsole ? (
                            <GroupManageConsoleHeader
                                tools={manageTools}
                                section={section}
                                onSectionChange={handleSectionChange}
                                onLeaveConsole={handleLeaveManageConsole}
                                localizer={localizer}
                            />
                        ) : null}

                        {!deniedAccess ? (
                            // The Back-to-top jump's anchor (FI-609): observed, and scrolled to.
                            <div ref={controlRowRef}>
                                <GroupContentOrderingControls
                                    orderingStyle={orderingStyle}
                                    onOrderingStyleChange={handleOrderingStyleChange}
                                    orderingOptions={orderingOptions}
                                    showOrderingControl={section === "discussions" && !withholdPostsNarrowing}
                                    questionsOnly={questionsOnly}
                                    onQuestionsOnlyChange={handleQuestionsOnlyChange}
                                    showQuestionsFilter={section === "discussions" && !withholdPostsNarrowing}
                                    localizer={localizer}
                                />
                            </div>
                        ) : null}

                        <>
                            {deniedAccess && (
                                banned ? (
                                    <BannedUserCardDisplayer banned={banned} localizer={localizer} />
                                ) : (
                                    /*
                                     * `ERRORS-24` — the refusal was the QUIETEST element on the page
                                     * it belongs to. It rendered as small muted centred text in a
                                     * bare `bg-card` strip: no icon, no heading, no height. Measured
                                     * on the captured desktop frame it was 54px tall while the
                                     * unrelated empty leader-queue panel 16px below it was 162px and
                                     * carried the only medallion and the only heading on the screen,
                                     * so someone scanning for *why can't I see this group* read the
                                     * loudest thing first and got an empty inbox.
                                     *
                                     * This is the design language's own panel recipe (rule 23, "The
                                     * state contract"): `bg-muted` fill — load-bearing, `bg-card`
                                     * measures 1.04:1 against the page and does not separate — a
                                     * solid hairline, `rounded-xl`, an icon medallion and the title
                                     * at heading weight. It deliberately does NOT reuse
                                     * `PaginatedListEmptyState`, whose shape this is: that component
                                     * carries `data-list-state` and `role="status" aria-live`, and
                                     * both are wrong here. This is not a list state, and it is not
                                     * the terminal state of a user-initiated change — it is what the
                                     * server rendered on arrival, so announcing it as a status change
                                     * would be a false live region.
                                     *
                                     * Both halves the wave-10 pass had to leave open are now here,
                                     * on wave-14 answer `Q10` ("a beginning: one panel"), which was
                                     * the parked `D11` product question — is the private-group wall a
                                     * refusal, or a beginning?
                                     *
                                     * `ERRORS-24` asked for one heading and one sentence: the panel
                                     * had a single string doing both jobs. It now leads with a short
                                     * heading naming the state and carries the explanation under it.
                                     *
                                     * BOTH strings already ship in all 25 catalogs, and that is a
                                     * deliberate composition rather than a shortcut. The obvious
                                     * pairing — `group.visibility.private` over `group.access_denied`
                                     * — reads "Private group" above "This is a private group. Request
                                     * to join to view posts and participate.", which is `ERRORS-7`'s
                                     * defect (the description repeating the headline) filed one
                                     * surface over. Pairing it with `group.visibility.private.description`
                                     * instead says the state once and then what to do about it, and it
                                     * settles `ERRORS-40` in the same move: that sentence used to be
                                     * the header's near-paraphrase OF this panel, and it is now the
                                     * page's single carrier, here, where the member is looking.
                                     * ⚠️ `group.access_denied` consequently has no frontend call site
                                     * left. It is not deleted — that is a 25-catalog edit and this
                                     * batch may not open a catalog — and the backend's separate
                                     * `group.error.private_group.access_denied` is unaffected.
                                     *
                                     * `ERRORS-24` also asked for the action IN the block, which was
                                     * blocked because on desktop the request control lives in the
                                     * rail and moving it here meant either two live "Request to join"
                                     * controls on one screen or taking the action row off the wall's
                                     * header. `Q10` chose the second: the header's duplicate yields
                                     * (`groupWallOwnsJoinActions`, whose whole rationale lives beside
                                     * it in `GroupPageHeaders`), and the `···` overflow travels with
                                     * it, so the page still offers Share and Report — in one place
                                     * rather than two.
                                     *
                                     * `ERRORS-25` asked for the expectation to be set in the same
                                     * block as the button rather than left to a label flipping to
                                     * "Request sent". It sits directly under the control.
                                     * ⛔ Its OTHER half — an onward step to public groups on the same
                                     * topic or to the directory — was explicitly NOT taken by `Q10`,
                                     * and neither was the question of whether a non-member should see
                                     * who runs a group they cannot enter. Do not add either here.
                                     */
                                    /*
                                     * `GROUP-72` (U24, 2026-09-28) supersedes the `bg-muted` fill above: `bg-card` and
                                     * `bg-muted` land on opposite sides of the page colour in the two themes, so the
                                     * gate and the identity card swapped visual weight between light and dark. The
                                     * identity keeps `bg-card`; the gate is a bordered FLAT card (page colour, the
                                     * solid hairline carries the edge), in both themes.
                                     */
                                    <div
                                        className="mb-4 flex w-full flex-col items-center gap-3 rounded-xl border border-border bg-transparent px-4 py-6 text-center"
                                        data-playwright-id="group-access-denied"
                                    >
                                        <div className="rounded-full bg-primary/10 p-3 text-primary">
                                            <Lock className="size-5" aria-hidden="true" />
                                        </div>
                                        {/* `GROUP-94` (r41 C3): the hero badge already says "Private group"
                                            inside the same viewport, so the heading spends its slot on
                                            the consequence for this viewer instead. The badge keeps the
                                            classification; `group.visibility.private` still labels the
                                            rail and the header chip. */}
                                        <h2 className="font-medium text-text-main">{localizer("group.visibility.private.door_title")}</h2>
                                        <p className="max-w-prose text-sm text-neutral-text">{localizer("group.visibility.private.description")}</p>
                                        {wallOwnsJoinActions ? (
                                            <div
                                                className="flex w-full max-w-xs flex-col items-center gap-2"
                                                data-playwright-id="group-access-denied-actions"
                                            >
                                                <GroupButtons
                                                    alignStart={true}
                                                    stretchPrimary={true}
                                                    groupPublicId={groupPublicId!}
                                                    groupSlug={groupSlug ?? ""}
                                                    initiallyMember={Boolean(initiallyMember)}
                                                    initiallyLeader={Boolean(initiallyLeader)}
                                                    viewerLeadershipLevel={viewerLeadershipLevel}
                                                    groupName={groupName ?? ""}
                                                    groupDescription={groupDescription ?? ""}
                                                    welcomeContent={groupWelcomeContent}
                                                    groupThumbnailUrl={groupThumbnailUrl ?? ""}
                                                    mainLocale={groupMainLocale ?? undefined}
                                                    discussionSubmissionPolicy={groupDiscussionSubmissionPolicy ?? undefined}
                                                    privateAccess={Boolean(groupPrivateAccess)}
                                                    sensitive={Boolean(groupSensitive)}
                                                    initiallyRequestedToJoin={Boolean(initiallyRequested)}
                                                    initiallyBanned={Boolean(initiallyBanned)}
                                                    compact={true}
                                                    // Deliberately unchanged from the header mount this
                                                    // replaces, so the join-request funnel keeps ONE
                                                    // series across the move: it is the same control,
                                                    // for the same group, on the same page.
                                                    surface="group_page_header"
                                                    localizer={localizer}
                                                />
                                                {/*
                                                  * `ERRORS-25`'s expectation, in the same block as the
                                                  * button. ONE line, two mutually exclusive branches,
                                                  * so the sentence is true in whichever state the
                                                  * member is looking at:
                                                  *
                                                  *  - before the request, `group.access-denied.request-expectation`
                                                  *    ("A group leader reviews each request.") — who
                                                  *    reviews it, and nothing more. Until round 38 D22
                                                  *    (2026-09-26, `ERRORS-52`) it added "You'll hear
                                                  *    back either way", backed only by both answer
                                                  *    notifications EXISTING; nothing makes an answer
                                                  *    arrive (SV-146's ignore is silent by design, and
                                                  *    only leaderless groups' requests expire), so the
                                                  *    promise was dropped rather than made true. Do not
                                                  *    re-add an answer guarantee here;
                                                  *  - once it is in, `group.request_sent_confirmation`
                                                  *    ("Request sent. A group leader will review it.").
                                                  *
                                                  * Rendering either one in the other's state would be
                                                  * false, which is why the pre-send half waited for a
                                                  * key of its own rather than reusing the post-send
                                                  * sentence. Wave 15 declared the name
                                                  * (`WAVE-15-DECLARED-KEYS/A1.5.md` §2) and it was
                                                  * already seeded in all 25 catalogs with no call site;
                                                  * this is the call site, and it cost no new key.
                                                  *
                                                  * ⛔ The two halves `Q10` left open stay open by
                                                  * decision, not by omission: NO onward step (public
                                                  * groups on the same topic, or the directory) and NO
                                                  * leadership block for a non-member. Do not add either
                                                  * here.
                                                  */}
                                                <p
                                                    className="text-xs leading-5 text-neutral-text"
                                                    data-playwright-id="group-access-denied-expectation"
                                                >
                                                    {/* `GROUP-71` (C17): an ANONYMOUS visitor is told the
                                                        real first step (an account), not only who reviews. */}
                                                    {initiallyRequested
                                                        ? localizer("group.request_sent_confirmation")
                                                        : isLoggedIn
                                                            ? localizer("group.access-denied.request-expectation")
                                                            : localizer("group.access-denied.anonymous-expectation")}
                                                </p>
                                            </div>
                                        ) : null}
                                    </div>
                                )
                            )}

                            {deniedAccess ? (
                                !banned && isLoggedIn && groupPublicId ? (
                                    <ViewerRequestsSection groupPublicId={groupPublicId} localizer={localizer} />
                                ) : null
                            ) : (
                                <>
                                    {section === "discussions" && (
                                        <>
                                            {pinnedDiscussions.length > 0 ? (
                                                <GroupFeaturedDiscussionsBlock
                                                    items={pinnedDiscussions}
                                                    groupPublicId={groupPublicId}
                                                    viewerIsLeaderOfGroup={Boolean(initiallyLeader)}
                                                    onPinnedDiscussionRemoved={handlePinnedDiscussionRemoved}
                                                    sensitiveMasking={sensitiveMasking}
                                                    localizer={localizer}
                                                />
                                            ) : null}
                                            <DiscussionsSection
                                                groupPublicId={groupPublicId}
                                                initiallyMember={initiallyMember}
                                                initiallyLeader={initiallyLeader}
                                                orderingStyle={orderingStyle}
                                                questionsOnly={questionsOnly}
                                                onClearQuestionsFilter={handleClearQuestionsFilter}
                                                onUnfilteredEmptinessSettled={setDiscussionsKnownEmpty}
                                                // Only the invite affordance on the empty state reads
                                                // these two (`EMPTY-STATES-47`); the list itself is
                                                // unchanged by them.
                                                privateAccess={groupPrivateAccess}
                                                viewerLeadershipLevel={viewerLeadershipLevel}
                                                currentPinnedDiscussions={pinnedDiscussions}
                                                onPinnedDiscussionAdded={handlePinnedDiscussionAdded}
                                                onPinnedDiscussionRemoved={handlePinnedDiscussionRemoved}
                                                prependDiscussions={prependDiscussions}
                                                removedDiscussionIds={removedDiscussionIds}
                                                sensitiveMasking={sensitiveMasking}
                                                // Round 38 D31 (`EMPTY-STATES-112`): a joinable non-member's
                                                // empty Posts panel leads with Join. The SAME control the
                                                // header mounts (one join handler, the private-request flow
                                                // and the `?join=1` sign-up continuation included), in the
                                                // primary tier and under its own hook so the header's
                                                // `group-join-toggle` stays unique. Private groups never
                                                // reach this list for a non-member (the wall shows instead).
                                                joinAction={groupPublicId && !initiallyMember && !initiallyLeader
                                                    && !initiallyBanned && !groupPrivateAccess ? (
                                                    <GroupButtons
                                                        displayMode="primary-only"
                                                        primaryOnlyTone="primary"
                                                        primaryOnlyAlign="center"
                                                        joinPlaywrightId="group-discussions-empty-join"
                                                        groupPublicId={groupPublicId}
                                                        groupSlug={groupSlug ?? ""}
                                                        initiallyMember={false}
                                                        initiallyLeader={false}
                                                        viewerLeadershipLevel={viewerLeadershipLevel}
                                                        groupName={groupName ?? ""}
                                                        groupDescription={groupDescription ?? ""}
                                                        welcomeContent={groupWelcomeContent}
                                                        groupThumbnailUrl={groupThumbnailUrl ?? ""}
                                                        mainLocale={groupMainLocale ?? undefined}
                                                        discussionSubmissionPolicy={groupDiscussionSubmissionPolicy ?? undefined}
                                                        privateAccess={false}
                                                        sensitive={Boolean(groupSensitive)}
                                                        initiallyRequestedToJoin={Boolean(initiallyRequested)}
                                                        initiallyBanned={false}
                                                        surface="group_discussions_empty"
                                                        localizer={localizer}
                                                    />
                                                ) : undefined}
                                                localizer={localizer}
                                            />
                                        </>
                                    )}
                                    {/* There is no conversation SECTION here any more. The board was a
                                        section of this island (never a second `data-component`
                                        container in group.jte — rule 32), and it has moved to
                                        Messages; `Open chat` in the tab bar is what replaces it. So
                                        this island loses a branch and gains no mount point, which is
                                        the one direction rule 32 is indifferent to. */}
                                    {section === "members" && (
                                        <MembersSection
                                            groupPublicId={groupPublicId}
                                            initiallyLeader={initiallyLeader}
                                            viewerLeadershipLevel={viewerLeadershipLevel}
                                            sensitiveMasking={sensitiveMasking}
                                            localizer={localizer}
                                        />
                                    )}
                                    {/* About owns everything the group says about itself: the governance
                                        rail content (description, rules, resources, related groups) plus
                                        the full leaders list, which used to be its own /leaders route. */}
                                    {section === "about" && (
                                        <div className="space-y-4">
                                            {initiallyLeader && groupPublicId ? (
                                                <GroupAboutVisitorPreviewBar
                                                    previewing={aboutAsVisitor}
                                                    onChange={setAboutAsVisitor}
                                                    localizer={localizer}
                                                />
                                            ) : null}
                                            <GroupRail
                                                welcomeContent={groupWelcomeContent}
                                                groupPublicId={groupPublicId}
                                                groupSlug={groupSlug}
                                                name={groupName}
                                                description={groupDescription}
                                                thumbnailUrl={groupThumbnailUrl} bannerUrl={groupBannerUrl}
                                                mainLocale={groupMainLocale}
                                                discussionSubmissionPolicy={groupDiscussionSubmissionPolicy}
                                                creatorUsername={initialCreatorUsername ?? null}
                                                creatorProfileImageUrl={initialCreatorProfileImageUrl ?? null}
                                                createdAt={initialCreatedAt ?? null}
                                                activity={initialActivity}
                                                discussionsCount={initialDiscussionsCount ?? null}
                                                privateAccess={groupPrivateAccess}
                                                initiallyMember={initiallyMember}
                                                initiallyRequested={initiallyRequested}
                                                initiallyBanned={initiallyBanned}
                                                initiallyLeader={aboutLeaderView}
                                                viewerLeadershipLevel={viewerLeadershipLevel}
                                                rules={groupRules}
                                                resources={groupResources}
                                                relatedGroups={groupRelatedGroups}
                                                leaders={groupLeaders}
                                                loading={groupLoading}
                                                error={groupError}
                                                onGroupSaved={handleGroupSaved}
                                                // `GROUP-59` (S7 U22): an empty Related groups card no longer
                                                // sits above Leaders; its add action follows Leaders below.
                                                deferEmptyRelatedGroups={true}
                                                localizer={localizer}
                                            />
                                            <LeadersSection
                                                groupPublicId={groupPublicId}
                                                initiallyLeader={aboutLeaderView}
                                                initialLeaders={hasInitialGovernanceBootstrap ? groupLeaders : undefined}
                                                localizer={localizer}
                                            />
                                            {aboutLeaderView && groupPublicId && !groupLoading && groupRelatedGroups.length === 0 ? (
                                                <GroupRelatedGroupsAddRow
                                                    groupPublicId={groupPublicId}
                                                    relatedGroups={groupRelatedGroups}
                                                    onGroupSaved={handleGroupSaved}
                                                    localizer={localizer}
                                                />
                                            ) : null}
                                            {/* `GROUP-75` (S7 U22): the About tab is the group's pitch, and a
                                                visitor persuaded by it used to finish ~3 phone screens below the
                                                only Join. A terminal action on the last card, not a sticky layer
                                                (which would cost height on every tab). Same control and gate as the
                                                empty Posts panel's Join (round 38 D31), under its own hook so the
                                                header's `group-join-toggle` stays unique. The header surface is
                                                reused for attribution: this is the same decision, one card lower. */}
                                            {groupPublicId && !initiallyMember && !initiallyLeader && !initiallyBanned && !groupPrivateAccess ? (
                                                <div className={MAIN_PAGE_LIST_WIDTH_CLASS} data-playwright-id="group-about-terminal-join">
                                                    <GroupButtons
                                                        displayMode="primary-only"
                                                        primaryOnlyTone="primary"
                                                        primaryOnlyAlign="center"
                                                        joinPlaywrightId="group-about-terminal-join-toggle"
                                                        groupPublicId={groupPublicId}
                                                        groupSlug={groupSlug ?? ""}
                                                        initiallyMember={false}
                                                        initiallyLeader={false}
                                                        viewerLeadershipLevel={viewerLeadershipLevel}
                                                        groupName={groupName ?? ""}
                                                        groupDescription={groupDescription ?? ""}
                                                        welcomeContent={groupWelcomeContent}
                                                        groupThumbnailUrl={groupThumbnailUrl ?? ""}
                                                        mainLocale={groupMainLocale ?? undefined}
                                                        discussionSubmissionPolicy={groupDiscussionSubmissionPolicy ?? undefined}
                                                        privateAccess={false}
                                                        sensitive={Boolean(groupSensitive)}
                                                        initiallyRequestedToJoin={Boolean(initiallyRequested)}
                                                        initiallyBanned={false}
                                                        surface="group_page_header"
                                                        localizer={localizer}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                    {/* `GROUP-82` (S7 U18): the console's landing index, where the entry pill lands. */}
                                    {section === "manage/overview" && initiallyLeader && (
                                        <GroupManageConsoleIndex
                                            tools={manageTools}
                                            onSelect={handleSectionChange}
                                            localizer={localizer}
                                        />
                                    )}
                                    {section === "manage/requests" && initiallyLeader && (
                                        <RequestsSection
                                            groupPublicId={groupPublicId}
                                            initiallyLeader={initiallyLeader}
                                            sensitiveMasking={sensitiveMasking}
                                            localizer={localizer}
                                        />
                                    )}
                                    {section === "manage/submissions" && initiallyLeader && groupPublicId && (
                                        <DiscussionSubmissionsSection
                                            groupPublicId={groupPublicId}
                                            localizer={localizer}
                                        />
                                    )}
                                    {section === "manage/invitations" && canManageInvitations && groupPublicId && (
                                        <InvitationsSection
                                            groupPublicId={groupPublicId}
                                            localizer={localizer}
                                        />
                                    )}
                                    {/* The fifth leader queue (2026-09-02). Gated on `canManageInvitations`
                                        and NOT on `initiallyLeader`, so it matches the service bar
                                        (MODERATOR+) rather than being one notch looser than the action
                                        it offers. A leader who can see the queue can clear it. */}
                                    {section === "manage/invite-requests" && canManageInvitations && groupPublicId && (
                                        <GroupInviteRequestsSection
                                            groupPublicId={groupPublicId}
                                            localizer={localizer}
                                            onResolved={() => adjustLeaderQueueCount("inviteRequests", -1)}
                                            onRestored={() => adjustLeaderQueueCount("inviteRequests", 1)}
                                        />
                                    )}
                                    {section === "manage/banned" && initiallyLeader && groupPublicId && (
                                        <GroupBannedUsersSection groupPublicId={groupPublicId} localizer={localizer} />
                                    )}
                                    {section === "manage/excluded" && initiallyLeader && groupPublicId && (
                                        <GroupExcludedDiscussionsSection groupPublicId={groupPublicId} localizer={localizer} />
                                    )}
                                    {section === "manage/reports" && initiallyLeader && groupPublicId && (
                                        <GroupReportsSection groupPublicId={groupPublicId} localizer={localizer} />
                                    )}
                                    {section === "manage/timeouts" && initiallyLeader && groupPublicId && (
                                        <GroupTimeoutsSection groupPublicId={groupPublicId} localizer={localizer} />
                                    )}
                                    {section === "manage/history" && initiallyLeader && groupPublicId && (
                                        <GroupModerationLogSection groupPublicId={groupPublicId} localizer={localizer} />
                                    )}
                                    {/* G15: settings as a deep-linkable surface rather than a dialog.
                                        Same form the dialog mounts, so validation and save behaviour
                                        cannot drift between the two entry points. */}
                                    {section === "manage/settings" && canEditSettings && groupPublicId && (
                                        <section
                                            className="rounded-2xl border border-border bg-card p-4"
                                            data-playwright-id="group-manage-settings"
                                        >
                                            <CreateGroupForm
                                                groupPublicId={groupPublicId}
                                                name={groupName ?? ""}
                                                description={groupDescription ?? ""}
                                                welcomeMessage={groupWelcomeMessage ?? ""}
                                                thumbnailUrl={groupThumbnailUrl ?? ""}
                                                bannerUrl={groupBannerUrl ?? ""}
                                                mainLocale={groupMainLocale ?? undefined}
                                                discussionSubmissionPolicy={groupDiscussionSubmissionPolicy}
                                                privateAccess={Boolean(groupPrivateAccess)}
                                                sensitive={groupSensitive}
                                                viewerIsOwner={viewerLeadershipLevel === "OWNER"}
                                                archived={archived}
                                                localizer={localizer}
                                                onSaved={handleGroupSettingsSaved}
                                            />
                                        </section>
                                    )}
                                </>
                            )}
                        </>
                    </div>
                )}
                rail={(
                    <GroupRail
                        groupPublicId={groupPublicId}
                        groupSlug={groupSlug}
                        name={groupName}
                        description={groupDescription}
                        thumbnailUrl={groupThumbnailUrl} bannerUrl={groupBannerUrl}
                        mainLocale={groupMainLocale}
                        discussionSubmissionPolicy={groupDiscussionSubmissionPolicy}
                        creatorUsername={initialCreatorUsername ?? null}
                        creatorProfileImageUrl={initialCreatorProfileImageUrl ?? null}
                        createdAt={initialCreatedAt ?? null}
                        activity={initialActivity}
                        discussionsCount={initialDiscussionsCount ?? null}
                        privateAccess={groupPrivateAccess}
                        initiallyMember={initiallyMember}
                        initiallyRequested={initiallyRequested}
                        initiallyBanned={initiallyBanned}
                        initiallyLeader={initiallyLeader}
                        viewerLeadershipLevel={viewerLeadershipLevel}
                        // `ERRORS-23` / `GROUP-19`, the desktop twin of the mobile header above:
                        // the rail forwards both straight through to `GroupDesktopRailHeader`'s
                        // three count chips and to nothing else.
                        onSectionChange={deniedAccess ? undefined : handleSectionChange}
                        buildSectionHref={deniedAccess ? undefined : buildSectionHref}
                        showDesktopRailHeader={true}
                        showDesktopRailControls={false}
                        // The About tab renders the governance sections in the main column, and the
                        // console is a focused surface — in both cases the rail keeps only the group's
                        // identity and join actions, so nothing is shown twice.
                        showSupplementarySections={!deniedAccess && section !== "about" && !inManageConsole}
                        rules={groupRules}
                        resources={groupResources}
                        relatedGroups={groupRelatedGroups}
                        leaders={groupLeaders}
                        loading={groupLoading}
                        error={groupError}
                        onGroupSaved={handleGroupSaved}
                        localizer={localizer}
                    />
                )}
            />
        </SensitivePageGate>
    );
}

export const mountGroupPage = createReactMount<GroupPageProps>({
    name: "mountGroupPage",
    getProps: ({ dataset, parsed, localizer }) => {
        const creatorUsername = normalizeDisplayUsername(dataset.creatorUsername);
        const bannedUsername = dataset.bannedUsername || undefined;
        // No bannedReason here: group.jte deliberately stops emitting data-banned-reason, because the
        // free text is a moderation note the owner wrote about this very viewer. The banned card
        // renders the structured reasonType instead. See the note above the element in group.jte.
        const bannedReasonType = dataset.bannedReasonType || undefined;
        // JTE emits data-banned-banned-at/-until/-by (dataset keys bannedBannedAt/Until/By) — the
        // doubled "banned" is intentional and must be matched here, exactly like the profile-image
        // sibling below. Reading the single-"banned" keys silently defaults these to undefined, which
        // renders a temporary ban as "permanent" / "unknown" and hides the moderator attribution.
        const bannedAt = dataset.bannedBannedAt || undefined;
        const bannedUntil = dataset.bannedBannedUntil || undefined;
        const bannedBy = dataset.bannedBannedBy || undefined;
        const bannedByProfileImageUrl = dataset.bannedBannedByProfileImageUrl || undefined;
        const bannedProfileImageUrl = dataset.bannedProfileImageUrl || undefined;
        const governanceBootstrap = normalizeGovernanceBootstrap(parsed.governanceBootstrap);

        const banned: BannedUserWithActorAvatar | null = bannedUsername ? {
            username: bannedUsername,
            reason: undefined,
            // @ts-ignore keep as any for display-only usage
            reasonType: bannedReasonType ? (bannedReasonType as any) : undefined,
            bannedAt: bannedAt ?? undefined,
            bannedUntil: bannedUntil ?? undefined,
            duration: undefined as any,
            bannedBy: bannedBy ?? undefined,
            bannedByProfileImageUrl: bannedByProfileImageUrl ?? undefined,
            profileImageUrl: bannedProfileImageUrl ?? undefined,
        } : null;

        return {
            initialSection: dataset.section ?? "discussions",
            groupPublicId: dataset.groupPublicId || undefined,
            groupSlug: dataset.groupSlug || undefined,
            initialName: dataset.name || undefined,
            initialDescription: dataset.description ?? null,
            initialThumbnailUrl: dataset.thumbnailUrl ?? null,
            initialBannerUrl: dataset.bannerUrl ?? null,
            mainLocale: dataset.mainLocale ?? null,
            initialDiscussionSubmissionPolicy: normalizeGroupDiscussionSubmissionPolicy(dataset.discussionSubmissionPolicy),
            initialCreatorUsername: creatorUsername,
            initialCreatorProfileImageUrl: dataset.creatorProfileImageUrl ?? null,
            initialCreatedAt: dataset.createdAt ?? null,
            initialActivity: readGroupActivityFromDataset(dataset),
            initialDiscussionsCount: dataset.discussionsCount ? Number(dataset.discussionsCount) : null,
            initiallyMember: dataset.initiallyMember === "true",
            initiallyLeader: dataset.initiallyLeader === "true",
            viewerLeadershipLevel: normalizeLeadershipLevel(dataset.viewerLeadershipLevel),
            initiallyRequested: dataset.initiallyRequestedToJoin === "true" || dataset.initiallyRequested === "true",
            initiallyBanned: dataset.initiallyBanned === "true",
            privateAccess: dataset.privateAccess === "true",
            sensitive: dataset.sensitive === "true",
            banned,
            deniedAccess: dataset.deniedAccess === "true",
            archived: dataset.archived === "true",
            initialGovernanceBootstrap: governanceBootstrap,
            localizer,
        };
    },
    render: (props) => <GroupPage {...props} />,
});
